import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowLeftIcon } from 'lucide-react';
import { ChatInterface } from './ChatInterface';
import { usePortfolio } from '../context/PortfolioContext';
import { useNavigate, useLocation } from 'react-router-dom';
interface ChatDrawerProps {
  onCaseStudyClick: (slug: string) => void;
}
export function ChatDrawer({
  onCaseStudyClick
}: ChatDrawerProps) {
  const {
    isChatDrawerOpen,
    closeChatDrawer,
    setNavigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  // Check if we're on a case study page
  const isCaseStudyPage = location.pathname.includes('/case-study/');
  // Handle case study click - navigate to page, keep chat open on desktop, close on mobile
  const handleCaseStudyClick = (slug: string) => {
    // CRITICAL: Reset scroll position IMMEDIATELY before navigation
    window.scrollTo(0, 0);
    // CRITICAL: Set sessionStorage flag that user came from chat
    sessionStorage.setItem('cameFromChat', 'true');
    console.log('✅ Set cameFromChat flag in sessionStorage');
    // Set flag that we're navigating from chat
    setNavigatedFromChat(true);
    // Navigate to the full case study page
    navigate(`/case-study/${slug}`);
    // Only close chat on mobile (to maximize screen space)
    if (window.innerWidth < 1024) {
      closeChatDrawer();
    }
    // On desktop: chat stays open so user can continue conversation
  };
  // Keyboard shortcut: ESC to close drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isChatDrawerOpen) {
        e.preventDefault();
        closeChatDrawer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isChatDrawerOpen, closeChatDrawer]);
  // Focus trap: Keep focus within drawer when open
  useEffect(() => {
    if (!isChatDrawerOpen || !drawerRef.current) return;
    const drawer = drawerRef.current;
    const focusableElements = drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    // Focus first element when drawer opens
    setTimeout(() => {
      firstElement?.focus();
    }, 100);
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        // Shift + Tab: moving backwards
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab: moving forwards
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    drawer.addEventListener('keydown', handleTabKey as any);
    return () => {
      drawer.removeEventListener('keydown', handleTabKey as any);
    };
  }, [isChatDrawerOpen]);
  // Prevent body scroll when drawer is open on mobile - WITHOUT causing scroll jump
  useEffect(() => {
    if (isChatDrawerOpen) {
      // Only prevent scroll on mobile
      if (window.innerWidth < 1024) {
        // Save current scroll position
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
      }
    } else {
      // Restore scroll position on mobile
      if (window.innerWidth < 1024) {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      // Cleanup
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isChatDrawerOpen]);
  // Announce drawer state to screen readers
  useEffect(() => {
    if (isChatDrawerOpen) {
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = 'TAIO Chat opened';
      document.body.appendChild(announcement);
      return () => {
        document.body.removeChild(announcement);
      };
    }
  }, [isChatDrawerOpen]);
  return <>
      {/* Mobile: Full-screen overlay with backdrop */}
      <AnimatePresence data-id="element-129">
        {isChatDrawerOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="lg:hidden fixed inset-0 bg-black/95" style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 99999998
      }} onClick={closeChatDrawer} aria-hidden="true" data-id="element-130" />}
      </AnimatePresence>

      {/* Desktop: Use fixed positioning on case study pages, sticky on portfolio */}
      <AnimatePresence data-id="element-131">
        {isChatDrawerOpen && <motion.div ref={drawerRef} initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: 420,
        opacity: 1
      }} exit={{
        width: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="hidden lg:flex flex-col bg-black border-l border-white/10 shadow-2xl flex-shrink-0 sticky top-0 h-screen overflow-hidden" role="dialog" aria-modal="true" aria-label="TAIO Chat" data-id="element-132">
            {/* Desktop Header */}
            <div className="flex-shrink-0 border-b border-white/10 p-4 flex items-center justify-between w-[420px]" data-id="element-133">
              <h2 id="drawer-title" className="text-white font-medium text-lg" style={{
            fontFamily: "'Gefika', sans-serif"
          }} data-id="element-134">
                AI Chat
              </h2>
              <button ref={firstFocusableRef} onClick={closeChatDrawer} className="text-white/60 hover:text-white focus:text-white transition-colors p-2 hover:bg-white/5 focus:bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Close AI Chat" data-id="element-135">
                <XIcon className="w-5 h-5" data-id="element-136" />
              </button>
            </div>

            {/* Chat Interface - Desktop */}
            <div className="flex-1 overflow-hidden w-[420px]" data-id="element-137">
              <ChatInterface onCaseStudyClick={handleCaseStudyClick} isSplitView={false} activeCaseStudy={null} data-id="element-138" />
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile: Full-screen fixed overlay - SIMPLIFIED (no case study viewer) */}
      <AnimatePresence data-id="element-139">
        {isChatDrawerOpen && <motion.div ref={drawerRef} initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="lg:hidden fixed right-0 top-0 h-full w-full bg-black" style={{
        zIndex: 99999999
      }} role="dialog" aria-modal="true" aria-label="TAIO Chat" data-id="element-140">
            {/* Chat Interface - Mobile (includes its own header with close button) */}
            <div className="h-full" data-id="element-141">
              <ChatInterface onCaseStudyClick={handleCaseStudyClick} isSplitView={false} activeCaseStudy={null} onClose={closeChatDrawer} data-id="element-142" />
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Screen reader only text */}
      <style data-id="element-143">{`
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }
      `}</style>
    </>;
}